import React, { useCallback } from 'react';
import { CollectionGridDeepLinkAsset, CollectionGridMediaAsset } from 'types';

import { UIDAPresentationTypeEnum } from '@kaltura-ott/tvpil';
import { UserActionType } from '@kaltura-ott/tvpil-analytics';
import { CategoryStore, CollectionAsset, CollectionLinkAsset, CollectionProgramAsset } from '@kaltura-ott/tvpil-shared';
import { ParentalPinStoreFacade } from '@kaltura-ott/tvpil-shared/lib/facades/parental-pin-store-facade';

import { useCardRecordInfo } from 'components/widgets/CardView/hooks/useCardRecordInfo/useCardRecordInfo';
import { DescriptionWithAge } from 'components/widgets/Modal/PinCodeModal/DescriptionWithAge/DescriptionWithAge';
import PinCodeModal from 'components/widgets/Modal/PinCodeModal/PinCodeModal';
import { PinCodeModalTypes } from 'components/widgets/Modal/PinCodeModal/PinCodeModalTypesEnum';
import { useRailContext } from 'components/widgets/Rail/components/RailProvider/RailProvider';
import { LAST_SCROLL_Y_POSITION } from 'consts';
import { useModalContext } from 'context/modal/modal';
import { useStableReactRouter } from 'context/stableReactRouterContext/stableReactRouterContext';
import {
  useAppFindPageStore,
  useIsUrlToFullPlayer,
  usePlayProviderAsset,
  useSettingsStore,
} from 'hooks';
import { globalStorage } from 'services/globalStorage';
import { resolvePathToDetailsPage, userActionEvents } from 'utils';

interface Props {
  data: CollectionAsset | CollectionProgramAsset | undefined;
  link: string;
  mediaTypeParam: string;
  isExternalProviderAsset: boolean;
  isLinkAsAsset: boolean;
  isPurchasable: boolean;
  collectionType: string | undefined;
  isProgramAsset: boolean;
  isTypeOfSeries: boolean;
  isCollectionEpisode: boolean;
  isCollectionInAppAdultLink: boolean;
  isCatchupSeries: boolean;
  imageHeight: number;
  isAdultBlocked: boolean;
  isRestricted: boolean;
  parentalPinStore: ParentalPinStoreFacade;
  hasNoDirectPlayback?: boolean;
}

const useCollectionCardClickHandler = ({
  data,
  link,
  mediaTypeParam,
  isExternalProviderAsset,
  isLinkAsAsset,
  isPurchasable,
  collectionType,
  isProgramAsset,
  isTypeOfSeries,
  isCollectionEpisode,
  isCollectionInAppAdultLink,
  imageHeight,
  isAdultBlocked,
  isRestricted,
  parentalPinStore,
  hasNoDirectPlayback,
}: Props) => {
  const { onCardClick } = useRailContext();
  const { findPageStore } = useAppFindPageStore();
  const { navigate } = useStableReactRouter();
  const { settingsStore } = useSettingsStore();
  const isUrlToFullPlayer = useIsUrlToFullPlayer(link) || false;
  const { parentalRating } = data as CollectionGridMediaAsset;
  const { isRecord, isSeriesType } = useCardRecordInfo({
    data,
    isProgramAsset,
    isTypeOfSeries,
    isCollectionEpisode,
  });

  // TODO: maybe change ref to css transform state
  const modalContext = useModalContext();
  const playProviderAsset = usePlayProviderAsset(data as CollectionGridDeepLinkAsset, modalContext);

  return useCallback(
    (e: React.MouseEvent<any>) => {
      globalStorage.clearDetailsAsset(); // Temporary solution to avoid opening a detail page with an asset saved in globalStorage

      sessionStorage.setItem(LAST_SCROLL_Y_POSITION, window.scrollY.toString());

      if (onCardClick) {
        onCardClick(data?.id!);
      }


      const page = link && (findPageStore(link.split('?')[0]) as CategoryStore);

      if (isExternalProviderAsset) {
        playProviderAsset(e, isExternalProviderAsset);
        return;
      }

      if (isRestricted) {
        e.preventDefault();
        e.stopPropagation();

        let type = PinCodeModalTypes.RestrictedContent;
        let inAppLink: string | undefined;

        if (isCollectionInAppAdultLink) {
          type = PinCodeModalTypes.RestrictedContentForLink;
          inAppLink = (data as CollectionLinkAsset)?.inAppLink;
        }

        const validatePin = (pin: string) => {
          if (isCollectionInAppAdultLink && inAppLink) {
            return parentalPinStore.validateInApplicationLink(pin, inAppLink);
          }

          return parentalPinStore.validateAdultPin(pin);
        };

        modalContext.openModal(
          <PinCodeModal
            callback={(success: boolean) => {
              if (success && isCollectionInAppAdultLink && link) {
                navigate(link);
              }
            }}
            context={UIDAPresentationTypeEnum.applications}
            type={type}
            validatePin={validatePin}
          />,
        );

        return;
      }

      if (isLinkAsAsset) {
        if (page && isAdultBlocked) {
          e.preventDefault();

          const validateAdult = (pin: string) => parentalPinStore.validateAdultPin(pin);

          modalContext.openModal(
            <PinCodeModal
              callback={(success: boolean) => {
                if (success && link) {
                  navigate(link);
                }
              }}
              context={UIDAPresentationTypeEnum.applications}
              type={PinCodeModalTypes.RestrictedContent}
              validatePin={validateAdult}
            />,
          );
          return;
        }
      }

      if (hasNoDirectPlayback || isPurchasable && !isRecord && !isSeriesType) {
        e.preventDefault();
        if (isLinkAsAsset) return;

        userActionEvents(UserActionType.details, data?.id, mediaTypeParam);

        navigate(
          resolvePathToDetailsPage({
            id: data?.id!,
            mediaTypeParam: mediaTypeParam.toLowerCase(),
            subtype: collectionType,
          }),
          {
            state: {
              lastScrollYPosition: window.scrollY + imageHeight,
            },
          },
        );
        return;
      }

      if (isUrlToFullPlayer && parentalRating && parentalPinStore.isBlockedByParentalRating(parentalRating)) {
        e.preventDefault();

        const validateParental = (pin: string) => parentalPinStore.validateParentalRating({ pin, parentalRating });

        modalContext.openModal(
          <PinCodeModal
            callback={(success: boolean) => {
              if (success && link) {
                navigate(link);
              }
            }}
            context={UIDAPresentationTypeEnum.applications}
            description={parentalRating && <DescriptionWithAge age={parentalRating} />}
            type={PinCodeModalTypes.RestrictedContentParental}
            validatePin={validateParental}
          />,
        );
        return;
      }

      // temp, better render <a> instead of <Link>
      if (link && link.includes('http')) {
        e.preventDefault();
        window.open(link, '_blank');
      }

      if (link && link.includes('details')) {
        // TODO: should be done in the next iteration
        // if (!(currentAsset instanceof LinkAsAsset)) {
        //   globalStorage.setDetailsAsset(currentAsset);
        // }
        // to send the user event after clicking on an AssetCardLink which should open the details page
        userActionEvents(UserActionType.details, data?.id, mediaTypeParam);
      }
    },
    [
      modalContext,
      link,
      parentalRating,
      isRecord,
      isSeriesType,
      settingsStore,
      isCollectionInAppAdultLink,
      isAdultBlocked,
      isRestricted,
      hasNoDirectPlayback,
    ],
  );
};

export { useCollectionCardClickHandler };
